import { useAuth } from "@/components/auth/auth-context";
import { useCallback, useEffect, useRef } from "react";
import { loadStytch } from "../stytch";
import { EventType } from "../types/events";

type EventCallback<T = unknown> = (event: T) => void;

interface EventListeners {
  [key: string]: Set<EventCallback>;
}

// Singleton event manager to maintain a single SSE connection
class EventManager {
  private static instance: EventManager;
  private eventSource: EventSource | null = null;
  private listeners: EventListeners = {};
  private connectionPromise: Promise<void> | null = null;
  private isConnecting = false;
  private reconnectAttempt = 0;
  private maxReconnectAttempts = 5;
  private baseReconnectDelay = 1000; // Start with 1 second
  private sessionJwt: string | null = null;
  private reconnectTimeout: NodeJS.Timeout | null = null;

  private constructor() {}

  static getInstance(): EventManager {
    if (!EventManager.instance) {
      EventManager.instance = new EventManager();
    }
    return EventManager.instance;
  }

  private getReconnectDelay(): number {
    // Exponential backoff with jitter
    const delay = Math.min(
      this.baseReconnectDelay * Math.pow(2, this.reconnectAttempt),
      30000, // Max delay of 30 seconds
    );
    // Add random jitter of ±25%
    return delay * (0.75 + Math.random() * 0.5);
  }

  private async reconnect() {
    if (
      this.reconnectAttempt >= this.maxReconnectAttempts ||
      !this.sessionJwt
    ) {
      console.error("Max reconnection attempts reached or no session token");
      this.disconnect();
      return;
    }

    this.reconnectAttempt++;
    const delay = this.getReconnectDelay();
    console.log(
      `Attempting to reconnect in ${delay}ms (attempt ${this.reconnectAttempt})`,
    );

    this.reconnectTimeout = setTimeout(async () => {
      try {
        await this.connect(this.sessionJwt!);
        this.reconnectAttempt = 0; // Reset on successful connection
      } catch (error) {
        console.error("Reconnection attempt failed:", error);
        this.reconnect();
      }
    }, delay);
  }

  async connect(sessionJwt: string) {
    if (this.isConnecting) {
      return this.connectionPromise;
    }

    // Store the session JWT for reconnection
    this.sessionJwt = sessionJwt;

    if (this.eventSource?.readyState === EventSource.OPEN) {
      return Promise.resolve();
    }

    // Clean up any existing connection
    this.disconnect();

    this.isConnecting = true;
    this.connectionPromise = new Promise((resolve, reject) => {
      try {
        this.eventSource = new EventSource(
          `${import.meta.env.VITE_API_URL}/v2/events/subscribe?token=${encodeURIComponent(sessionJwt)}`,
          { withCredentials: true },
        );

        this.eventSource.onmessage = (event) => {
          try {
            const data = JSON.parse(event.data);

            // Silently handle heartbeat messages
            if (data.type === "HEARTBEAT") {
              return;
            }

            // 🔍 GLOBAL SSE MESSAGE DEBUGGING
            console.log("🌍 GLOBAL SSE MESSAGE RECEIVED:");
            console.log("  Raw event.data:", event.data);
            console.log("  Parsed data.type:", data.type);
            console.log("  Parsed data.payload:", data.payload);
            console.log("  Available listeners:", Object.keys(this.listeners));
            console.log(
              "  Listeners for this type:",
              this.listeners[data.type]?.size || 0,
            );

            const eventListeners = this.listeners[data.type];
            if (eventListeners) {
              console.log(
                `🎯 Found ${eventListeners.size} listeners for ${data.type}, calling them...`,
              );
              eventListeners.forEach((callback) => callback(data.payload));
            } else {
              console.log(`❌ No listeners found for event type: ${data.type}`);
            }
          } catch (error) {
            console.error("Error processing event:", error);
          }
        };

        this.eventSource.onopen = () => {
          this.isConnecting = false;
          this.reconnectAttempt = 0; // Reset reconnect attempts on successful connection
          console.log("SSE connection established");
          resolve();
        };

        this.eventSource.onerror = (error) => {
          console.log("WHY ARE WE GETTING AN ERORR HERE?");
          console.log(this.eventSource);
          console.error("EventSource error:", error);
          this.isConnecting = false;

          if (this.eventSource?.readyState === EventSource.CLOSED) {
            console.log("Connection closed, attempting to reconnect...");
            this.eventSource.close();
            this.eventSource = null;
            this.reconnect();
          }

          reject(error);
        };
      } catch (error) {
        this.isConnecting = false;
        reject(error);
      }
    });

    return this.connectionPromise;
  }

  addEventListener<T = unknown>(
    eventType: EventType,
    callback: EventCallback<T>,
  ) {
    if (!this.listeners[eventType]) {
      this.listeners[eventType] = new Set();
    }
    this.listeners[eventType].add(callback as EventCallback);

    return () => {
      this.listeners[eventType]?.delete(callback as EventCallback);
      if (this.listeners[eventType]?.size === 0) {
        delete this.listeners[eventType];
      }

      // If there are no more listeners at all, close the connection
      if (Object.keys(this.listeners).length === 0) {
        this.disconnect();
      }
    };
  }

  disconnect() {
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }

    if (this.eventSource) {
      this.eventSource.close();
      this.eventSource = null;
    }

    this.listeners = {};
    this.isConnecting = false;
    this.connectionPromise = null;
    this.reconnectAttempt = 0;
    this.sessionJwt = null;
  }
}

export function useEvents() {
  const { user } = useAuth();
  const eventManager = useRef(EventManager.getInstance());

  useEffect(() => {
    if (!user) {
      eventManager.current.disconnect();
      return;
    }

    const client = loadStytch();
    const tokens = client.session.getTokens();

    if (!tokens?.session_jwt) {
      console.error("No session token available");
      return;
    }

    eventManager.current.connect(tokens.session_jwt).catch((error) => {
      console.error("Failed to establish SSE connection:", error);
    });

    return () => {
      // Don't disconnect on unmount if there are other components using the connection
      // The EventManager will handle cleanup when all listeners are removed
    };
  }, [user]);

  const addEventListener = useCallback(
    <T = unknown>(eventType: EventType, callback: EventCallback<T>) => {
      return eventManager.current.addEventListener(eventType, callback);
    },
    [],
  );

  return { addEventListener };
}
